import React from 'react';
import { FaTags, FaWarehouse, FaBarcode, FaPills, FaStethoscope, FaFlask, FaBoxes, FaSync } from 'react-icons/fa';
import { useLanguage } from '../contexts/LanguageContext';
import CategoryFilter from './CategoryFilter';
import SupplyCategoryFilter from './SupplyCategoryFilter';
import SortimentCodeFilter from './SortimentCodeFilter';
import MiGeLFilter from './MiGeLFilter';
import ATCFilter from './ATCFilter';
import GenerikaFilter from './GenerikaFilter';
import CompanyDropdown from './CompanyDropdown';

// Mirrors the shop's consumerCategoryId composition (see ProductManager).
const buildConsumerCategoryId = (categoryEn, subcategoryEn) =>
  subcategoryEn ? `${categoryEn}_${subcategoryEn}` : categoryEn;

// One styled filter card — same visual language as the shop's filter panel.
const FilterCard = ({ title, description, gradient, icon, children }) => (
  <div className="bg-white border border-gray-200 rounded-xl p-4">
    <div className="flex items-center justify-between gap-4">
      <div className="flex-1">{children}</div>
      <div className="flex items-center gap-3">
        <div className="text-right">
          <h4 className="font-semibold text-gray-800">{title}</h4>
          <p className="text-sm text-gray-600">{description}</p>
        </div>
        <div className={`w-10 h-10 bg-gradient-to-br ${gradient} rounded-xl flex items-center justify-center shadow-sm`}>
          {icon}
        </div>
      </div>
    </div>
  </div>
);

/**
 * Catalog filter panel. Renders the SAME filter components the shop uses, in
 * the shop's card style, but the selections are COMBINABLE (AND): each filter
 * updates one key on the `filters` object via onChange(key, value); clearing a
 * filter calls onChange(key, ''). Wiring stays dumb — the parent
 * (CatalogProductList) holds state and triggers the fetch.
 */
const CatalogFilters = ({ filters, onChange, onClearAll }) => {
  const { t } = useLanguage();
  const hasAny = Object.values(filters).some(Boolean);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Consumer Category */}
        <FilterCard
          title={t('products.filters.category.title')}
          description={t('products.filters.category.description')}
          gradient="from-emerald-100 to-green-100"
          icon={<FaTags className="text-emerald-600 text-lg" />}
        >
          <CategoryFilter
            onCategorySelect={(cat, subcat) => onChange('consumerCategoryId', buildConsumerCategoryId(cat, subcat))}
            onClear={() => onChange('consumerCategoryId', '')}
          />
        </FilterCard>

        {/* Supply Category */}
        <FilterCard
          title={t('products.filters.supply.title')}
          description={t('products.filters.supply.description')}
          gradient="from-blue-100 to-indigo-100"
          icon={<FaWarehouse className="text-blue-600 text-lg" />}
        >
          <SupplyCategoryFilter
            onCategorySelect={(cat) => onChange('supplyCategoryCode', cat)}
            onClear={() => onChange('supplyCategoryCode', '')}
          />
        </FilterCard>

        {/* Sortiment */}
        <FilterCard
          title={t('products.filters.sortiment.title')}
          description={t('products.filters.sortiment.description')}
          gradient="from-amber-100 to-yellow-100"
          icon={<FaBarcode className="text-amber-600 text-lg" />}
        >
          <SortimentCodeFilter
            onSortimentCodeSelect={(code, subcode) => onChange('sortimentCode', subcode || code)}
            onClear={() => onChange('sortimentCode', '')}
          />
        </FilterCard>

        {/* MiGeL */}
        <FilterCard
          title={t('products.filters.migel.title')}
          description={t('products.filters.migel.description')}
          gradient="from-purple-100 to-pink-100"
          icon={<FaPills className="text-purple-600 text-lg" />}
        >
          <MiGeLFilter
            onMiGeLCodeSelect={(code, subcode) => onChange('migelCode', subcode || code)}
            onClear={() => onChange('migelCode', '')}
          />
        </FilterCard>

        {/* ATC */}
        <FilterCard
          title={t('products.filters.atc.title')}
          description={t('products.filters.atc.description')}
          gradient="from-cyan-100 to-blue-100"
          icon={<FaStethoscope className="text-cyan-600 text-lg" />}
        >
          <ATCFilter
            onATCCodeSelect={(code, subcode) => onChange('atcCode', subcode || code)}
            onClear={() => onChange('atcCode', '')}
          />
        </FilterCard>

        {/* Generika */}
        <FilterCard
          title={t('products.filters.generika.title')}
          description={t('products.filters.generika.description')}
          gradient="from-orange-100 to-red-100"
          icon={<FaFlask className="text-orange-600 text-lg" />}
        >
          <GenerikaFilter
            onGenerikaCodeSelect={(code, subcode) => onChange('genericCode', subcode || code)}
            onClear={() => onChange('genericCode', '')}
          />
        </FilterCard>

        {/* Company */}
        <FilterCard
          title={t('products.filters.company.title')}
          description={t('products.filters.company.description')}
          gradient="from-slate-100 to-gray-200"
          icon={<FaBoxes className="text-slate-600 text-lg" />}
        >
          <div className="flex items-center gap-2">
            <CompanyDropdown
              onSelect={(company) => onChange('company', company || '')}
              selectedCompany={filters.company}
            />
            {filters.company && (
              <button
                onClick={() => onChange('company', '')}
                className="ml-2 text-xs text-gray-500 underline"
              >
                {t('products.filters.clear')}
              </button>
            )}
          </div>
        </FilterCard>
      </div>

      {hasAny && (
        <div className="pt-1">
          <button
            onClick={onClearAll}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 px-5 py-2.5 rounded-xl font-medium hover:from-gray-200 hover:to-gray-300 transition-all duration-200 shadow-sm border border-gray-300"
          >
            <FaSync className="text-gray-500" />
            {t('products.filters.clearAll')}
          </button>
        </div>
      )}
    </div>
  );
};

export default CatalogFilters;
